import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  ViewChild,
} from "@angular/core";
import * as d3 from "d3";
import { fitAxisLabel } from "./moonlake-axis-labels";
import { AggRow } from "./moonlake-aggregate";

// ===========================================================================
// Grouped bars / lines, drawn the way the waterfall draws its stacked bars
//
// The sibling of moonlake-waterfall-chart for page 2: the same margins, axes,
// dashed grid, y-axis zoom slider, brush strip and dark tooltip, so a card on
// either page reads as the same widget. What differs is the marks — one bar per
// series side by side in each group, or one smooth line per series through the
// groups' centres. Rows come from aggregateSeries(): `<key>` is the plotted
// value, `<key>__n` / `<key>__d` the count behind it.
// ===========================================================================

export interface SeriesChartConfig {
  type: "bar" | "line";
  /** Series, in legend order. */
  keys: string[];
  colours: Record<string, string>;
  /** Tooltip suffix per series, e.g. "Forecast". */
  keyNotes?: Record<string, string>;
  xAxisLabel: string;
  yAxisLabel: string;
  /** "pct": values are shares (0–100). "abs": counts. */
  yMode: "pct" | "abs";
  chartHeight: number;
  /** Unit noun for the counts in the tooltip ("lives", "patients"). */
  unit: string;
  valueFormat: (v: number) => string;
  /**
   * Tooltip: each value's share of the group, and the group total in the head.
   * Off for values that do not add up — an index, an average. Default on.
   */
  showShare?: boolean;
  noDataMessage?: string;
}

const FONT = "Poppins";
const AXIS_LINE = "#d1d5db";
const GRID_LINE = "#e5e7eb";
const AXIS_LABEL = "#101d42";
const TICK_LABEL = "#101d42";
const SLIDER_TRACK = "#e5e7eb";
const SLIDER_ACCENT = "#1363df";
const BRUSH_FILL = "#A0A0A0";
const BRUSH_STROKE = "#E8EAEE";
const BAR_GAP = 4;

let nextId = 0;

@Component({
  selector: "app-moonlake-series-chart",
  template: `<div class="mlwf"><div #chartEl class="mlwf-chart"></div></div>`,
  // The waterfall's sheet: the chart chrome and the body-level .mlwf-tt tooltip.
  styleUrls: ["./moonlake-waterfall-chart.component.scss"],
  standalone: false,
})
export class MoonlakeSeriesChartComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() data: AggRow[] = [];
  @Input() config!: SeriesChartConfig;
  @Input() hiddenKeys: Set<string> = new Set();

  @ViewChild("chartEl", { static: true }) chartEl!: ElementRef<HTMLElement>;

  readonly divId = `mlsc${nextId++}`;

  handleRadius = 6;
  lineMin = 0;
  lineMax = 100;

  private viewReady = false;
  private resizeObs?: ResizeObserver;
  private resizeFrame = 0;
  private lastWidth = 0;

  private tooltipEl: HTMLDivElement | null = null;
  private boundMouseMove = (e: MouseEvent) => {
    if (this.tooltipEl?.classList.contains("show")) this.positionTooltip(e);
  };

  constructor(private zone: NgZone) {}

  ngAfterViewInit(): void {
    this.tooltipEl = document.createElement("div");
    this.tooltipEl.className = "mlwf-tt";
    document.body.appendChild(this.tooltipEl);

    this.zone.runOutsideAngular(() => {
      document.addEventListener("mousemove", this.boundMouseMove);
      this.initiateChart();
      this.viewReady = true;
      this.redraw();

      this.resizeObs = new ResizeObserver((entries) => {
        const w = Math.round(entries[0]?.contentRect.width || 0);
        if (!w || w === this.lastWidth) return;
        this.lastWidth = w;
        cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = requestAnimationFrame(() => this.redraw());
      });
      this.resizeObs.observe(this.chartEl.nativeElement);
    });
  }

  ngOnChanges(): void {
    if (!this.viewReady) return;
    this.zone.runOutsideAngular(() => this.redraw());
  }

  ngOnDestroy(): void {
    document.removeEventListener("mousemove", this.boundMouseMove);
    this.resizeObs?.disconnect();
    cancelAnimationFrame(this.resizeFrame);
    this.tooltipEl?.remove();
    this.tooltipEl = null;
  }

  // ── helpers ──
  private get isAbs(): boolean {
    return this.config.yMode === "abs";
  }

  private visibleKeys(): string[] {
    return this.config.keys.filter((k) => !this.hiddenKeys.has(k));
  }

  private value(row: AggRow, key: string): number {
    return Number(row[key]) || 0;
  }

  private yMaxFor(maxValue: number): number {
    if (!this.isAbs) return 100;
    if (!(maxValue > 0)) return 1;
    return d3.scaleLinear().domain([0, maxValue]).nice().domain()[1] as number;
  }

  private tickFormat(d: number): string {
    return this.isAbs ? this.config.valueFormat(d) : Math.round(d) + "%";
  }

  private gradId(i: number): string {
    return `sgrad_${i}_${this.divId}`;
  }

  /** Each series' fill: its colour, darkening a touch toward the base — the waterfall's bars. */
  private createGradientDefs(): void {
    const defs = d3.select("#svg_" + this.divId).select("defs");
    defs.selectAll("linearGradient").remove();
    this.config.keys.forEach((key, i) => {
      const hex = this.config.colours[key] || "#999999";
      const darken = (c: number) => Math.round(c * 0.85).toString(16).padStart(2, "0");
      const [r, g, b] = [1, 3, 5].map((o) => parseInt(hex.slice(o, o + 2), 16));
      const grad = defs
        .append("linearGradient")
        .attr("id", this.gradId(i))
        .attr("x1", "0%")
        .attr("y1", "0%")
        .attr("x2", "0%")
        .attr("y2", "100%");
      grad.append("stop").attr("offset", "0%").attr("stop-color", hex);
      grad
        .append("stop")
        .attr("offset", "100%")
        .attr("stop-color", "#" + darken(r) + darken(g) + darken(b));
    });
  }

  // ── tooltip ──
  private showTooltip(row: AggRow, event: MouseEvent): void {
    if (!this.tooltipEl) return;
    const c = this.config;
    const unit = c.unit;
    const total = c.keys.reduce((s, k) => s + this.value(row, k), 0);
    const share = c.showShare !== false;
    const lines = c.keys
      .map((k) => {
        const dim = this.hiddenKeys.has(k) ? "opacity:.35;" : "";
        const note = c.keyNotes?.[k] ? ` <span style="opacity:.7">(${c.keyNotes[k]})</span>` : "";
        const v = this.value(row, k);
        // % view: the count behind the share, then the share. Count view: the
        // count, then its share of the group.
        const count = this.isAbs ? v : Number(row[`${k}__n`]) || 0;
        const pct = this.isAbs ? (total ? Math.round((v / total) * 100) : 0) : Math.round(v);
        const pctCell = share ? `<span class="tt-p">${pct}%</span>` : "";
        return `<div class="tt-r" style="${dim}"><span class="tt-d" style="background:${c.colours[k]}"></span><span class="tt-l">${k}${note}</span><span class="tt-v">${c.valueFormat(count)}</span>${pctCell}</div>`;
      })
      .join("");
    const head = this.isAbs && share ? `${row.name} — ${c.valueFormat(total)} ${unit}` : String(row.name);
    this.tooltipEl.innerHTML = `<div class="tt-hd">${head}</div><div class="tt-body">${lines}</div>`;
    this.tooltipEl.classList.add("show");
    this.positionTooltip(event);
  }

  private hideTooltip(): void {
    this.tooltipEl?.classList.remove("show");
  }

  private positionTooltip(e: MouseEvent): void {
    if (!this.tooltipEl) return;
    const r = this.tooltipEl.getBoundingClientRect();
    const gap = 16;
    const flip = e.clientX + gap + r.width > window.innerWidth - 10;
    const x = flip ? e.clientX - r.width - gap : e.clientX + gap;
    const y = Math.min(Math.max(10, e.clientY - r.height / 2), window.innerHeight - r.height - 10);
    this.tooltipEl.classList.toggle("tt-at-left", flip);
    this.tooltipEl.classList.toggle("tt-at-right", !flip);
    this.tooltipEl.style.left = x + "px";
    this.tooltipEl.style.top = y + "px";
  }

  // ── one-time scaffolding (the waterfall's initiateCharts) ──
  private initiateChart(): void {
    const id = this.divId;
    const svg = d3
      .select(this.chartEl.nativeElement)
      .append("svg")
      .attr("id", "svg_" + id)
      .attr("width", "100%")
      .style("background-color", "white");

    svg.append("text").attr("id", "noDataMessage" + id);

    const defs = svg.append("defs");
    defs.append("clipPath").attr("id", "brushClip" + id).append("rect").attr("id", "brushClipRect" + id);
    defs.append("clipPath").attr("id", "xAxisClip" + id).append("rect").attr("id", "xAxisClipRect" + id);

    const filter = defs
      .append("filter")
      .attr("id", "drop-shadow-brush-" + id)
      .attr("width", "200%")
      .attr("height", "200%");
    filter.append("feGaussianBlur").attr("in", "SourceAlpha").attr("stdDeviation", 1).attr("result", "blur");
    filter.append("feOffset").attr("in", "blur").attr("dx", 1).attr("dy", 1).attr("result", "offsetBlur");
    filter
      .append("feFlood")
      .attr("in", "offsetBlur")
      .attr("flood-color", "#000000")
      .attr("flood-opacity", 0.4)
      .attr("result", "offsetColor");
    filter
      .append("feComposite")
      .attr("in", "offsetColor")
      .attr("in2", "offsetBlur")
      .attr("operator", "in")
      .attr("result", "offsetBlur");
    const merge = filter.append("feMerge");
    merge.append("feMergeNode").attr("in", "offsetBlur");
    merge.append("feMergeNode").attr("in", "SourceGraphic");

    svg.append("g").attr("id", "yAxis" + id).attr("class", "axis" + id);
    svg.append("g").attr("id", "xAxis" + id).attr("class", "axis" + id);
    svg.append("text").attr("id", "yAxisLabel" + id);
    svg.append("text").attr("id", "xAxisLabel" + id);
    svg.append("g").attr("id", "chartGroup" + id);
    svg.append("g").attr("id", "brushGroup" + id);
    ["handleLeftLine1", "handleLeftLine2", "handleRightLine1", "handleRightLine2"].forEach((c) =>
      svg.append("line").attr("class", "handleLines" + id + " " + c + id)
    );
    svg.append("g").attr("id", "sliderGroup" + id);
  }

  // ── y-axis zoom slider (the waterfall's createRangeSlider) ──
  private createRangeSlider(margins: any, chartHeight: number, targetScale: any, yMax: number, onChange: () => void): void {
    const me = this;
    const slider = d3.select("#svg_" + this.divId).select("#sliderGroup" + this.divId);
    slider.selectAll("*").remove();
    const g: any = slider.append("g");
    const xPos = margins.left;
    const scale = d3.scaleLinear().domain([0, 100]).range([chartHeight, 0]).clamp(true);
    const at = "translate(" + xPos + "," + margins.top + ")";

    const move = (type: string, pos: number) => {
      g.select(".s-" + type + "-handle").attr("cy", pos);
      g.select(".s-" + type + "-hover").attr("cy", pos);
    };
    const onDrag = (event: any, type: "min" | "max") => {
      const pos = event.y - margins.top;
      const gap = 5;
      if (type === "min") {
        const p = Math.min(scale(0), Math.max(scale(100), pos));
        const v = scale.invert(p);
        if (v >= 0 && v <= me.lineMax - gap) {
          me.lineMin = v;
          move("min", p);
        }
      } else {
        const p = Math.max(scale(100), Math.min(scale(0), pos));
        const v = scale.invert(p);
        if (v <= 100 && v >= me.lineMin + gap) {
          me.lineMax = v;
          move("max", p);
        }
      }
      g.select(".s-inset").attr("y1", scale(me.lineMin)).attr("y2", scale(me.lineMax));
      targetScale.domain([(me.lineMin * yMax) / 100, (me.lineMax * yMax) / 100]);
      onChange();
    };

    g.append("line")
      .attr("y1", scale(0))
      .attr("y2", scale(100))
      .attr("transform", at)
      .attr("stroke", SLIDER_TRACK)
      .attr("stroke-width", 3)
      .attr("stroke-linecap", "round");
    g.append("line")
      .attr("class", "s-inset")
      .attr("y1", scale(this.lineMin))
      .attr("y2", scale(this.lineMax))
      .attr("transform", at)
      .attr("stroke", SLIDER_ACCENT)
      .attr("stroke-width", 3)
      .attr("stroke-linecap", "round");

    (["min", "max"] as const).forEach((type) => {
      const v = type === "min" ? this.lineMin : this.lineMax;
      const hover = g
        .append("circle")
        .attr("class", "s-" + type + "-hover")
        .attr("cy", scale(v))
        .attr("transform", at)
        .attr("r", this.handleRadius + 8)
        .attr("fill", SLIDER_ACCENT)
        .attr("opacity", 0)
        .attr("pointer-events", "none");
      const handle = g
        .append("circle")
        .attr("class", "s-" + type + "-handle")
        .attr("cy", scale(v))
        .attr("transform", at)
        .attr("r", this.handleRadius)
        .attr("fill", SLIDER_ACCENT)
        .attr("stroke", "white")
        .attr("stroke-width", 2)
        .style("cursor", "ns-resize")
        .on("mousedown", () => handle.raise())
        .call(d3.drag().on("drag", (event: any) => onDrag(event, type)));
      handle
        .on("mouseover", () => hover.transition().duration(200).attr("opacity", 0.4))
        .on("mouseout", () => hover.transition().duration(400).attr("opacity", 0));
    });
  }

  // ── plot ──
  private redraw(): void {
    if (!this.viewReady || !this.config) return;
    this.createGradientDefs();
    this.plot();
  }

  private plot(): void {
    const me = this;
    const id = me.divId;
    const props = me.config;
    const svg: any = d3.select("#svg_" + id);
    const width: number = svg.node().getBoundingClientRect().width;
    const height = props.chartHeight;
    svg.attr("height", height);

    const margins: any = { left: 64, right: 20, top: 24, bottom: 6, mid: 62, brush: 52 };
    const chartHeight = height - margins.top - margins.bottom - margins.mid - margins.brush;
    const chartWidth = width - margins.left - margins.right;
    const rows: AggRow[] = me.data || [];
    const keys = me.visibleKeys();
    const empty = rows.length === 0;
    const isLine = props.type === "line";

    svg
      .select("#noDataMessage" + id)
      .attr("x", margins.left + chartWidth / 2)
      .attr("y", margins.top + chartHeight / 2)
      .attr("font-family", FONT)
      .attr("fill", AXIS_LABEL)
      .attr("font-size", "14px")
      .attr("font-weight", "500")
      .attr("text-anchor", "middle")
      .text(empty ? props.noDataMessage || "There is no data for this selection" : "");

    const yMax = me.yMaxFor(d3.max(rows, (r) => d3.max(keys, (k) => me.value(r, k)) || 0) || 0);
    const xScaleAll: any = d3.scaleLinear().domain([0, rows.length]).range([0, chartWidth]);
    const xScale: any = d3.scaleLinear().domain([0, rows.length]).range([0, chartWidth]);
    const yScale: any = d3
      .scaleLinear()
      .domain([(me.lineMin * yMax) / 100, (me.lineMax * yMax) / 100])
      .range([chartHeight, 0]);

    svg
      .select("#xAxis" + id)
      .attr("transform", "translate(" + margins.left + "," + (margins.top + chartHeight) + ")")
      .attr("clip-path", "url(#xAxisClip" + id + ")");

    svg
      .select("#yAxisLabel" + id)
      .attr("visibility", empty ? "hidden" : "visible")
      .attr("transform", "translate(14," + (margins.top + chartHeight / 2) + ") rotate(-90)")
      .attr("text-anchor", "middle")
      .attr("font-family", FONT)
      .attr("fill", AXIS_LABEL)
      .attr("font-size", "11px")
      .text(props.yAxisLabel);

    svg
      .select("#xAxisLabel" + id)
      .attr("visibility", empty ? "hidden" : "visible")
      .attr("x", margins.left + chartWidth / 2)
      .attr("y", margins.top + chartHeight + 46)
      .attr("text-anchor", "middle")
      .attr("font-family", FONT)
      .attr("fill", AXIS_LABEL)
      .attr("font-size", "13px")
      .text(props.xAxisLabel);

    // Lines need a few pixels above the plot so a 3px stroke at 100% is not cut.
    svg
      .select("#brushClipRect" + id)
      .attr("x", isLine ? -4 : 0)
      .attr("y", -margins.top)
      .attr("width", chartWidth + (isLine ? 8 : 0))
      .attr("height", margins.top + chartHeight + (isLine ? 4 : 0));
    svg
      .select("#xAxisClipRect" + id)
      .attr("x", isLine ? -30 : 0)
      .attr("y", 0)
      .attr("width", chartWidth + (isLine ? 60 : 0))
      .attr("height", margins.mid);
    svg.select("#chartGroup" + id).attr("clip-path", "url(#brushClip" + id + ")");

    // ── brush: a miniature of the whole chart under it ──
    const startRows = Math.min(Math.floor(chartWidth / 80), rows.length);
    const brushStartX = empty ? 0 : (1 - startRows / rows.length) * chartWidth;
    const brush: any = d3
      .brushX()
      .handleSize(10)
      .extent([
        [0, 0],
        [chartWidth, margins.brush],
      ])
      .on("start brush end", brushed);

    const brushGroup = svg.select("#brushGroup" + id);
    brushGroup.selectAll(".brushMini" + id).remove();
    const yBrush = d3.scaleLinear().domain([0, yMax]).range([margins.brush, 0]);
    const miniGroupW = rows.length ? chartWidth / rows.length : 0;
    if (isLine) {
      keys.forEach((k) => {
        const line = d3
          .line<AggRow>()
          .x((_, i) => xScaleAll(i) + miniGroupW / 2)
          .y((r) => yBrush(me.value(r, k)))
          .curve(d3.curveMonotoneX);
        brushGroup
          .insert("path", ":first-child")
          .attr("class", "brushMini" + id)
          .attr("d", line(rows))
          .attr("fill", "none")
          .attr("stroke", props.colours[k])
          .attr("stroke-width", 1.5);
      });
    } else {
      const geo = me.barGeometry(miniGroupW, keys.length);
      rows.forEach((r, i) =>
        keys.forEach((k, j) => {
          const v = me.value(r, k);
          brushGroup
            .insert("rect", ":first-child")
            .attr("class", "brushMini" + id)
            .attr("x", xScaleAll(i) + geo.offset + j * (geo.bar + geo.gap))
            .attr("y", yBrush(v))
            .attr("width", Math.max(geo.bar, 1))
            .attr("height", Math.max(margins.brush - yBrush(v), 0.5))
            .attr("fill", props.colours[k]);
        })
      );
    }

    brushGroup
      .attr("transform", "translate(" + margins.left + "," + (margins.top + chartHeight + margins.mid) + ")")
      .attr("visibility", empty ? "hidden" : "visible")
      .call(brush)
      .call(brush.move, [brushStartX, chartWidth]);
    brushGroup.select(".overlay").style("fill", "transparent");
    brushGroup
      .selectAll(".selection")
      .attr("fill", BRUSH_FILL)
      .attr("fill-opacity", "0.2")
      .style("stroke", BRUSH_STROKE)
      .style("stroke-width", "1");

    svg
      .selectAll(".handleLines" + id)
      .attr("visibility", empty ? "hidden" : "visible")
      .attr("y1", margins.top + chartHeight + margins.mid + (margins.brush - 12) / 2)
      .attr("y2", margins.top + chartHeight + margins.mid + 12 + (margins.brush - 12) / 2)
      .style("stroke", "#8A98AB")
      .style("stroke-width", "1")
      .attr("transform", "translate(" + margins.left + ",0)");

    drawChart();

    function brushed(event: any): void {
      if (event.selection === null) return;
      xScale.domain(event.selection.map(xScaleAll.invert));
      drawChart();
      const bg = svg.select("#brushGroup" + id);
      const e = +bg.select(".handle--e").attr("x");
      const w = +bg.select(".handle--w").attr("x");
      svg.select(".handleLeftLine1" + id).attr("x1", e + 3).attr("x2", e + 3);
      svg.select(".handleLeftLine2" + id).attr("x1", e + 7).attr("x2", e + 7);
      svg.select(".handleRightLine1" + id).attr("x1", w + 3).attr("x2", w + 3);
      svg.select(".handleRightLine2" + id).attr("x1", w + 7).attr("x2", w + 7);
      bg.selectAll(".handle")
        .attr("fill", "white")
        .attr("rx", 4)
        .attr("ry", 4)
        .attr("y", (margins.brush - 24) / 2)
        .attr("height", 24)
        .style("filter", "url(#drop-shadow-brush-" + id + ")");
    }

    function updateYAxis(): void {
      svg
        .select("#yAxis" + id)
        .call(d3.axisLeft(yScale).ticks(5).tickSizeOuter(0).tickFormat((d: any) => me.tickFormat(d)))
        .attr("transform", "translate(" + margins.left + "," + margins.top + ")")
        .attr("visibility", empty ? "hidden" : "visible");
      svg.select("#yAxis" + id + " path").style("stroke", "none");
      svg
        .selectAll(".axis" + id + " text")
        .style("font-weight", 500)
        .style("font-family", FONT)
        .style("font-size", "11px")
        .style("fill", TICK_LABEL);
      svg
        .selectAll("#yAxis" + id + " line")
        .attr("x1", 0)
        .attr("x2", chartWidth)
        .style("stroke", GRID_LINE)
        .style("stroke-dasharray", "4,4")
        .style("stroke-width", 1);
    }

    function drawChart(): void {
      const visibleGroups = xScale.domain()[1] - xScale.domain()[0];
      const groupWidth = chartWidth / visibleGroups;
      updateYAxis();

      const chartGroup = svg.select("#chartGroup" + id);
      chartGroup.selectAll("*").remove();
      chartGroup.attr("transform", "translate(" + margins.left + "," + margins.top + ")");

      const xAxisGroup = svg.select("#xAxis" + id);
      xAxisGroup.selectAll("*").remove();
      if (empty) return;
      xAxisGroup
        .append("line")
        .attr("x1", 0)
        .attr("x2", chartWidth)
        .attr("y1", 0)
        .attr("y2", 0)
        .style("stroke", AXIS_LINE);

      const [d0, d1] = xScale.domain();
      rows.forEach((row, i) => {
        if (i < Math.floor(d0) || i >= Math.ceil(d1)) return;
        xAxisGroup
          .append("text")
          .attr("x", xScale(i) + groupWidth / 2)
          .attr("y", 6)
          .attr("dy", "1em")
          .style("text-anchor", "middle")
          .attr("fill", TICK_LABEL)
          .attr("font-weight", "500")
          .attr("font-size", "11px")
          .attr("font-family", FONT)
          .each(function (this: SVGTextElement) {
            fitAxisLabel(this, String(row.name), groupWidth);
          });
      });

      const [lo, hi] = yScale.domain();
      const yOf = (v: number) => yScale(Math.max(lo, Math.min(hi, v)));

      if (isLine) {
        // A guide at the hovered group, under the lines.
        const guide = chartGroup
          .append("line")
          .attr("y1", 0)
          .attr("y2", chartHeight)
          .style("stroke", AXIS_LINE)
          .style("stroke-dasharray", "3,3")
          .style("opacity", 0);

        keys.forEach((k) => {
          const i = me.config.keys.indexOf(k);
          const line = d3
            .line<AggRow>()
            .x((_, j) => xScale(j) + groupWidth / 2)
            .y((r) => yOf(me.value(r, k)))
            .curve(d3.curveMonotoneX);
          chartGroup
            .append("path")
            .attr("d", line(rows))
            .attr("fill", "none")
            .attr("stroke", props.colours[k] || "#999")
            .attr("stroke-width", 3)
            .attr("stroke-linecap", "round")
            .attr("data-key", i);
        });

        const dots = chartGroup.append("g").style("opacity", 0);
        rows.forEach((row, j) => {
          chartGroup
            .append("rect")
            .attr("x", xScale(j))
            .attr("y", 0)
            .attr("width", groupWidth)
            .attr("height", chartHeight)
            .attr("fill", "transparent")
            .on("mouseover", (event: MouseEvent) => {
              const cx = xScale(j) + groupWidth / 2;
              guide.attr("x1", cx).attr("x2", cx).style("opacity", 1);
              dots.selectAll("*").remove();
              keys.forEach((k) =>
                dots
                  .append("circle")
                  .attr("cx", cx)
                  .attr("cy", yOf(me.value(row, k)))
                  .attr("r", 4.5)
                  .attr("fill", props.colours[k])
                  .attr("stroke", "#fff")
                  .attr("stroke-width", 2)
              );
              dots.style("opacity", 1).raise();
              me.showTooltip(row, event);
            })
            .on("mouseout", () => {
              guide.style("opacity", 0);
              dots.style("opacity", 0);
              me.hideTooltip();
            });
        });
        return;
      }

      const geo = me.barGeometry(groupWidth, keys.length);
      rows.forEach((row, j) => {
        const g = chartGroup
          .append("g")
          .attr("transform", "translate(" + (xScale(j) + geo.offset) + ",0)")
          .style("cursor", "default");
        keys.forEach((k, n) => {
          const v = me.value(row, k);
          const top = yOf(v);
          const bottom = yOf(0);
          if (bottom <= top) return;
          g.append("rect")
            .attr("x", n * (geo.bar + geo.gap))
            .attr("y", top)
            .attr("width", geo.bar)
            .attr("height", Math.max(bottom - top, 0.5))
            .attr("fill", "url(#" + me.gradId(me.config.keys.indexOf(k)) + ")");
        });
        // The whole cluster, gaps included, answers the hover.
        g.insert("rect", ":first-child")
          .attr("x", 0)
          .attr("y", 0)
          .attr("width", Math.max(geo.cluster, 1))
          .attr("height", chartHeight)
          .attr("fill", "transparent");
        g.on("mouseover", (event: MouseEvent) => me.showTooltip(row, event)).on("mouseout", () =>
          me.hideTooltip()
        );
      });
    }

    this.createRangeSlider(margins, chartHeight, yScale, yMax, drawChart);
  }

  /** One group's bars: each ≤ 26px with a 4px gap, the cluster centred in its slot. */
  private barGeometry(groupWidth: number, n: number): { bar: number; gap: number; cluster: number; offset: number } {
    const count = Math.max(n, 1);
    const gap = groupWidth > 60 ? BAR_GAP : 1;
    const bar = Math.max(1, Math.min((groupWidth * 0.65 - gap * (count - 1)) / count, 26));
    const cluster = bar * count + gap * (count - 1);
    return { bar, gap, cluster, offset: (groupWidth - cluster) / 2 };
  }
}
